import { useEffect, useMemo, useState } from 'react'
import pick from 'lodash/pick'
import { observable, transaction } from 'mobx'
import { useObserver } from 'mobx-react-lite'

import { ActionRequired, ICoupon, ICustomer, ISubscriptionPlan, Subscription } from '../types'
import createRestClient from './createRestClient'
import useOrganization from './useOrganization'
import usePlan from './usePlan'

/**
 * Represents a raw subscription object coming from Billing API.
 * @internal
 */
interface ISubscription {
  id: string
  customerId: string
  status: string
  quantity: number
  created: number
  currentPeriodEnd: number
  currentPeriodStart: number
  startDate: number
  totalAmount: number
  cancelAtPeriodEnd?: boolean
  plan: ISubscriptionPlan
  coupon?: ICoupon
  actionRequired?: ActionRequired
  scheduledUpdate?:
    | {
        quantity?: number
        plan: ISubscriptionPlan
        totalAmount: number
      }
    | { quantity: number; totalAmount: number }
}

const store = observable<
  Partial<{
    customer: ICustomer | null
    subscription: ISubscription | null
  }>
>({
  customer: undefined,
  subscription: undefined,
})

export default function useBillingInfo() {
  const organization = useOrganization()
  const [error, setError] = useState<Error | null>(null)
  const customer = useObserver(() => store.customer)

  const planName = useObserver(() => {
    return store.subscription === null ? null : store.subscription?.plan.name
  })
  const currentPlan = usePlan(planName)

  const subscriptionInternal = useObserver(() => store.subscription)
  const subscription = useMemo((): Subscription | null | undefined => {
    if (subscriptionInternal === undefined || currentPlan === undefined) {
      return undefined
    }

    if (subscriptionInternal === null || currentPlan === null) {
      return null
    }

    const { plan, ...subscriptionWithoutPlan } = subscriptionInternal
    return {
      ...subscriptionWithoutPlan,
      currentPlan: {
        ...plan,
        ...currentPlan,
      },
    }
  }, [subscriptionInternal, currentPlan])

  useEffect(() => {
    if (customer === undefined && subscription === undefined && organization) {
      fetchBillingInfo(organization.stripeCustomerId)
    }
  }, [organization])

  async function updateCustomer(payload: Omit<ICustomer, 'defaultCard'>) {
    if (!organization) {
      return
    }

    const customer = await createRestClient(organization.workspaceId).patch<ICustomer>(
      `customers/${organization.stripeCustomerId}`,
      {
        json: payload,
      },
    )

    store.customer = customer
  }

  async function createSubscription(payload: {
    address?: ICustomer['address']
    token?: string
    planId: string
    quantity: number
    coupon: string
  }) {
    if (!organization) {
      return
    }

    const newSubscription = await createRestClient(organization.workspaceId).post<ISubscription>(
      'subscriptions',
      { json: payload },
    )

    // Update customer as well as subscription
    await fetchBillingInfo(newSubscription.customerId)

    return getSubscriptionEffects(store.subscription)
  }

  async function updateSubscription(
    payload:
      | { cancelAtPeriodEnd: boolean }
      | {
          quantity: number
          planId?: string
          coupon?: string
        },
  ) {
    if (!subscription || !organization) {
      return
    }

    const newSubscription = await createRestClient(organization.workspaceId).patch<ISubscription>(
      `subscriptions/${subscription.id}`,
      { json: payload },
    )

    store.subscription = newSubscription

    return getSubscriptionEffects(store.subscription)
  }

  async function cancelScheduledChange(subscriptionId: string) {
    if (!organization) {
      return
    }

    const newSubscription = await createRestClient(organization.workspaceId).delete<ISubscription>(
      `subscriptions/${subscriptionId}/scheduled-update`,
    )

    store.subscription = newSubscription

    return getSubscriptionEffects(store.subscription)
  }

  async function fetchBillingInfo(customerId: string | null) {
    await transaction(async () => {
      if (!organization) {
        return
      }

      if (!customerId) {
        store.customer = null
        store.subscription = null

        return
      }

      try {
        const { customer, subscription } = await createRestClient(organization.workspaceId).get<{
          customer: ICustomer
          subscription: ISubscription | undefined
        }>(`customers/${customerId}/info`)

        store.customer = customer ?? null
        store.subscription = subscription ?? null
      } catch (error_) {
        setError(error_ as Error)
      }
    })

    return getSubscriptionEffects(store.subscription)
  }

  if (error) {
    throw error
  }

  return {
    customer,
    subscription,
    updateCustomer,
    fetchBillingInfo,
    createSubscription,
    updateSubscription,
    cancelScheduledChange,
  }
}

// Do not return `ISubscription` as it is supposed to be internal
// Could have returned `Subscription` type but it is hard to merge it with `currentPlan` field coming from `usePlan` hook, therefore returning only 2 fields is enough for our current use cases
function getSubscriptionEffects(subscription: ISubscription | null | undefined) {
  return subscription ? pick(subscription, 'actionRequired', 'customerId') : subscription
}
